import React from "react";
import {
  Stack,
  Group,
  Select,
  Button,
  Text,
  TextInput,
  ActionIcon,
  Box,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import { getEntityLabel, getRelatedSetValueTargets } from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

const SELF_ENTITY = "__self__";

const McaSetValuesEditor = ({
  setValues = [],
  onChange,
  statusFields = [],
  targetEntity,
  description = "Applied to the target entity when this step is approved.",
}) => {
  const readOnly = useMcaEditorReadOnly();
  const relatedTargets = getRelatedSetValueTargets(targetEntity);
  const updateRow = (idx, patch) => {
    if (readOnly) return;
    const next = setValues.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const fieldsForRow = (row) => {
    if (row.targetEntity) {
      return (
        relatedTargets.find((target) => target.entity === row.targetEntity)
          ?.fields ?? []
      );
    }
    return statusFields;
  };

  const fieldDef = (row) => {
    const fields = fieldsForRow(row);
    return (
      fields.find((item) => item.field === row.field) ??
      (row.field ? { field: row.field, label: row.field, options: [] } : null)
    );
  };

  const fieldSelectData = (row) => {
    const fields = fieldsForRow(row);
    const known = fields.map((item) => ({
      value: item.field,
      label: item.label,
    }));
    if (row.field && !fields.some((item) => item.field === row.field)) {
      known.push({ value: row.field, label: row.field });
    }
    return known;
  };

  const entitySelectData = [
    {
      value: SELF_ENTITY,
      label: getEntityLabel(targetEntity) || "This record",
    },
    ...relatedTargets.map((target) => ({
      value: target.entity,
      label: target.label,
    })),
  ];

  if (!statusFields.length && !relatedTargets.length) {
    return (
      <Text size="xs" color="dimmed">
        Select a target entity with status fields to configure set values.
      </Text>
    );
  }

  return (
    <Stack spacing="xs" sx={{ width: "100%" }}>
      {description ? (
        <Text size="xs" color="dimmed">
          {description}
        </Text>
      ) : null}
      {setValues.map((row, idx) => {
        const def = fieldDef(row);
        const valueOptions = def?.options ?? [];
        const valueFromLabel = def?.valueFrom ? def.valueLabel || def.valueFrom : "";
        return (
          <Group key={idx} spacing="sm" align="flex-end" noWrap sx={{ width: "100%" }}>
            {relatedTargets.length > 0 && (
              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Select
                  placeholder="Record"
                  size="xs"
                  sx={{ width: "100%" }}
                  data={entitySelectData}
                  value={row.targetEntity || SELF_ENTITY}
                  onChange={(v) =>
                    updateRow(idx, {
                      targetEntity: !v || v === SELF_ENTITY ? "" : v,
                      field: "",
                      value: "",
                      valueFrom: "",
                    })
                  }
                  readOnly={readOnly}
                  searchable
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Select
                placeholder="Status field"
                size="xs"
                sx={{ width: "100%" }}
                data={fieldSelectData(row)}
                value={row.field || null}
                onChange={(v) => {
                  const nextDef = fieldsForRow({
                    ...row,
                    field: v ?? "",
                  }).find((item) => item.field === v);
                  updateRow(idx, {
                    field: v ?? "",
                    value: "",
                    valueFrom: nextDef?.valueFrom || "",
                  });
                }}
                readOnly={readOnly}
                searchable
                clearable
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              {valueFromLabel ? (
                <TextInput
                  placeholder="Value"
                  size="xs"
                  sx={{ width: "100%" }}
                  value={valueFromLabel}
                  readOnly
                />
              ) : (
                <Select
                  placeholder="Value"
                  size="xs"
                  sx={{ width: "100%" }}
                  data={valueOptions}
                  value={row.value ?? ""}
                  onChange={(v) => updateRow(idx, { value: v ?? "" })}
                  searchable
                  clearable
                  readOnly={readOnly}
                  disabled={!row.field}
                />
              )}
            </Box>
            {!readOnly && (
            <ActionIcon
              variant="subtle"
              size="sm"
              sx={{ flexShrink: 0 }}
              aria-label="Remove set value"
              onClick={() => onChange(setValues.filter((_, i) => i !== idx))}
            >
              <IconTrash size={14} />
            </ActionIcon>
            )}
          </Group>
        );
      })}
      {!readOnly && (
      <Button
        size="xs"
        w="fit-content"
        variant="light"
        leftIcon={<IconPlus size={14} />}
        onClick={() => onChange([...setValues, { field: "", value: "" }])}
      >
        Add set value
      </Button>
      )}
    </Stack>
  );
};

export default McaSetValuesEditor;
